import * as React from "react";

import {Box, Button, Container, Grid, Typography} from "@mui/material";

import {BLOCKS} from "@contentful/rich-text-types";
import {documentToReactComponents} from "@contentful/rich-text-react-renderer";
import HeroHome from "../components/blocks/HeroHome";
import Hero from "../components/blocks/Hero";
import HeroBlog from "../components/blocks/HeroBlog";
import Link from "next/link";
import ArrowRightIcon from "../components/icons/ArrowRightIcon";
import styled from "styled-components";
import {media} from "../../styles/styles";
import {useDispatch} from "react-redux";
import {actions as appActions} from "../redux/app";
import ArticleFeed from "../components/blocks/ArticleFeed";
import {scrollToId} from "../lib/helpers";
import ChequeredStrip from "../components/blockComponents/ChequeredStrip";
import TeamMembers from "../components/blocks/TeamMembers";
import ArrowLink from "../components/blockComponents/ArrowLink";
import {Event} from '../../utils/gtm';
import {useRouter} from 'next/router'
import {FbEvent} from '../../utils/fbq';

const rtfRenderOptions = {
  renderNode: {
    [BLOCKS.HEADING_6]: (node, children) => <sub>{children}</sub>,
  },
};

interface ColumnProps {
  size?: string;
  textAlign?: "left" | "right" | "center";
  flexValue?: number;
  grey?: boolean;
  marginTop?: boolean;
  minWidth?: string;
}

interface RowProps {
  breakpoint?: "desktop" | "tablet" | "phone";
}

const ArrowDown = styled(ArrowRightIcon)`
  position: absolute;
  bottom: -10px;
  transform: rotate(90deg);
  left: calc(50% - 6px);

  path {
    fill: #e55934;
  }
`;
const NavLink = styled.div`
  cursor: pointer;
  padding-bottom: 10px;

  svg {
    transition: bottom 200ms;
  }

  &:hover {
    svg {
      bottom: -20px;
    }
  }
`;
const OfferCard = styled.div`
  ul {
    p {
      margin: 0;
    }
  }
`;
const OfferCardGrid = styled(Grid)`
  && {
    margin-top: 15px;
    margin-bottom: 15px;
  }
`;
const QuoteGrid = styled(Grid)`
  && {
    ${media.phone`
     flex-direction:column-reverse;
     flex-wrap:wrap;
  `};
  }
`;
const FeatureCardGrid = styled(Grid)`
  && {
    max-width: 400px;
    padding: 0 2rem;
    ${media.tablet`
    padding:0 1rem;
  `};
    ${media.phone`
    padding: 1rem;
  `};
  }
`;
const QuoteImgWrapper = styled.div`
  flex: 1;
  margin-top: -3%;
  font-size: 0px;

  img {
    width: 100%;
    height: auto;
  }
`;
const StyledButton = styled(Button)`
  &.MuiButton-root {
    background-color: #de4a22;
    color: #fff;

    &:hover {
      background-color: #fff;
      color: #de4a22;
    }
  }
`;
const PadlessButton = styled(Button)`
  && {
    padding: 0;
  }
`;
const Quote = styled.div`
  padding: 1rem;
  font-size: 1rem;

  b {
    font-size: 1.3em;
  }

  p:last-of-type {
    margin-bottom: 0;
  }
`;
const LargeText = styled.div`
  font-size: 1.25rem;
`;
const BlockTitle = styled.h3`
  font-size: 1.4rem;
`;
const PhoneNumberWrapper = styled.div`
  margin: 1em 0;

  .phone_number {
    &:hover {
      color: #E55934;
      text-decoration: underline;
    }
  }
`;
const SmallContainer = styled(Container)`
  &.MuiContainer-maxWidthLg {
    max-width: 830px;
  }
`;
const Column = styled('div').withConfig({
  shouldForwardProp: (prop) => !['size', 'textAlign', 'flexValue', 'grey', 'marginTop', 'minWidth'].includes(prop)
})<ColumnProps>`
  padding: ${({size}) => size || "1rem"};
  ${({minWidth}) => minWidth && `min-width:${minWidth};`}
  ${({textAlign}) => (textAlign ? `text-align: ${textAlign};` : null)}
  ${({
       flexValue,
     }) => (flexValue ? `flex: ${flexValue};` : null)}
  ${({textAlign}) => {
    if (textAlign === "right") return media.tablet`text-align:center;`;
  }}
  ${({grey}) =>
      grey
          ? `&>div{background-color: #F0F0F0;border-radius:6px;padding:1rem 2rem;}`
          : null};
  flex-direction: column;
  justify-content: center;
  ${media.tablet`padding:.5rem 0;`}
  font-size: 1rem;
`;

const FeatuedImage = styled.img`
  max-width: 100%;
  min-width: 112px;
  ${media.tablet`
     padding:1rem;
     max-height: 200px;
     width:100%;
  `};
`;
const AlignedRow = styled('div').withConfig({
  shouldForwardProp:(prop) => !['breakpoint'].includes(prop)
})<RowProps>`
  display: flex;
  flex-grow: 1;
  width: 100%;
  align-items: center;
  position: relative;
  ${({breakpoint}) => {
    switch (breakpoint) {
      case "phone":
        return media.phone`
     display:block;
  `;
      case "desktop":
        return media.desktop`
     display:block;
  `;
      default:
        return media.tablet`
     display:block;
  `;
    }
  }}
`;
const Row = styled.div<RowProps>`
  display: flex;
  flex-grow: 1;
  width: 100%;
  ${media.tablet`
     display:block;
  `};
  z-index: 2;
  position: relative;
`;
const CenteredGrid = styled(Grid)`
  && {
    text-align: center;
    width: 100%;
    position: relative;
  }
`;
const PageContainerBlocks = (
    blocks,
    sectionIndex,
    app,
    posts,
    template,
    teamMembers,
    category
) => {
  const dispatch = useDispatch();
  useRouter();
  return blocks
      .map(({fields: block}, blockIndex) => {
        if (block.states && !block.states.includes(app.locationState.longName))
          return null;
        switch (block.blockType) {
          case "Hero":
            if (template === "Home")
              return (
                  <HeroHome
                      hero={block}
                      key={`section-${sectionIndex}_block-${blockIndex}`}
                  />
              );
            else if (template === "Post")
              return (
                  <HeroBlog
                      hero={block}
                      key={`section-${sectionIndex}_block-${blockIndex}`}
                      category={category}
                  />
              );
            else
              return (
                  <Hero
                      hero={block}
                      key={`section-${sectionIndex}_block-${blockIndex}`}
                  />
              );
          case "Quote":
            return (
                <SmallContainer key={`section-${sectionIndex}_block-${blockIndex}`}>
                  <QuoteGrid container alignItems="center">
                    {block.featureImage && (
                        <CenteredGrid item sm={6} xs={12}>
                          <QuoteImgWrapper>
                            <picture>
                              <source
                                  type="image/webp"
                                  srcSet={`${block.featureImage.fields.file.url}?fm=webp&w=375&q=80&h=236&fit=thumb`}
                                  media="(max-width: 400px)"
                              />
                              <source
                                  type="image/webp"
                                  srcSet={`${block.featureImage.fields.file.url}?fm=webp&w=375&q=80&h=236&fit=thumb`}
                                  media="(min-width: 401px)"
                              />
                              <source
                                  type="image/jpeg"
                                  srcSet={`${block.featureImage.fields.file.url}?w=375&q=80&h=236&fit=thumb`}
                                  media="(max-width: 400px)"
                              />
                              <source
                                  type="image/jpeg"
                                  srcSet={`${block.featureImage.fields.file.url}?w=375&q=80&h=236&fit=thumb`}
                                  media="(min-width: 401px)"
                              />
                              <img
                                  src={`${block.featureImage.fields.file.url}?w=375&q=80&h=236&fit=thumb`}
                                  alt={block.featureImage.fields.title}
                                  width="375"
                                  height="236"
                              />
                            </picture>
                          </QuoteImgWrapper>
                        </CenteredGrid>
                    )}
                    <Grid item sm={6} xs={12}>
                      <Quote>
                        {block.content
                            ? documentToReactComponents(block.content)
                            : null}
                        {block.callToAction && (
                            <a href={block.callToAction.fields.linkUrl}>
                              {block.callToAction.fields.linkTitle}
                            </a>
                        )}
                      </Quote>
                    </Grid>
                  </QuoteGrid>
                </SmallContainer>
            );
          case "Offer Card":
            return (
                <OfferCardGrid item
                    sm={6} xs={12}
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                >
                  <Grid container spacing={2}>
                    <CenteredGrid item sm={4}>
                      {block.featureImage && (
                          <FeatuedImage
                              src={block.featureImage.fields.file.url}
                              alt={block.featureImage.fields.title}
                          />
                      )}
                    </CenteredGrid>
                    <Grid item sm={8}>
                      <Typography
                          component="h3"
                          variant="h3"
                          color="inherit"
                          gutterBottom
                      >
                        {block.title}
                      </Typography>
                      <OfferCard>
                        {block.content
                            ? documentToReactComponents(
                                block.content,
                                rtfRenderOptions
                            )
                            : null}
                      </OfferCard>
                      {block.callToAction &&
                          block.callToAction.fields.linkUrl !== "contact" && (
                              <Link href={block.callToAction.fields.linkUrl}>
                                <span>
                                  <ArrowLink>
                                    {block.callToAction.fields.linkTitle}
                                  </ArrowLink>
                                </span>
                              </Link>
                          )}
                      {block.callToAction &&
                          block.callToAction.fields.linkUrl === "contact" && (
                              <PadlessButton
                                  variant="text"
                                  onClick={() => dispatch(appActions.toggleModal())}
                              >
                                <ArrowLink>
                                  {block.callToAction.fields.linkTitle}
                                </ArrowLink>
                              </PadlessButton>
                          )}
                    </Grid>
                  </Grid>
                </OfferCardGrid>
            );
          case "Feature Card":
            return (
                <FeatureCardGrid item
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                    sm={6}
                >
                  <AlignedRow breakpoint="desktop">
                    <Column size="1rem 0">
                      <Typography
                          component="h3"
                          variant="h3"
                          color="inherit"
                          gutterBottom
                      >
                        {block.title}
                      </Typography>
                    </Column>
                    <Box sx={{display: {smDown: `none`}}}>
                      <Column textAlign="center">
                        {block.featureImage && (
                            <FeatuedImage
                                src={block.featureImage.fields.file.url}
                                alt={block.featureImage.fields.title}
                            />
                        )}
                      </Column>
                    </Box>
                  </AlignedRow>
                  {block.content ? documentToReactComponents(block.content) : null}
                  {block.callToAction &&
                      block.callToAction.fields.linkUrl !== "contact" && (
                          <Link href={block.callToAction.fields.linkUrl}>
                            <span>
                              <ArrowLink>
                                {block.callToAction.fields.linkTitle}
                              </ArrowLink>
                            </span>
                          </Link>
                      )}
                  {block.callToAction &&
                      block.callToAction.fields.linkUrl === "contact" && (
                          <PadlessButton
                              variant="text"
                              onClick={() => dispatch(appActions.toggleModal())}
                          >
                            <ArrowLink>{block.callToAction.fields.linkTitle}</ArrowLink>
                          </PadlessButton>
                      )}
                </FeatureCardGrid>
            );
          case "Contact Banner":
            return (
                <div
                    style={{
                      position: "relative",
                      paddingTop: 30,
                      width: "100%",
                    }}
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                >
                  <ChequeredStrip location="top"/>
                  <Column flexValue={1}>
                    <Container>
                      <AlignedRow breakpoint="desktop">
                        <Column textAlign="left" flexValue={1} size="0">
                          <BlockTitle>{block.title}</BlockTitle>
                          <LargeText>
                            {block.content
                                ? documentToReactComponents(block.content)
                                : null}
                          </LargeText>
                        </Column>
                        <Column textAlign="right" flexValue={1}>
                          <LargeText>
                            {block.callToAction && (
                                <StyledButton
                                    variant="contained"
                                    color="secondary"
                                    disableElevation
                                    onClick={() => dispatch(appActions.toggleModal())}
                                >
                                  {block.callToAction.fields.linkTitle}
                                </StyledButton>
                            )}
                            <br/>
                            <PhoneNumberWrapper>or call {` `}
                              <a className="phone_number" href={`tel:${app.phoneNumber}`} onClick={() => {
                                FbEvent('Contact');
                                Event({
                                  action: 'click',
                                  category: 'phone call tracking',
                                  event_label: 'Phone Call',
                                  event_action: 'Click to Call'
                                })
                              }}>
                                {app.phoneNumber}
                              </a>
                            </PhoneNumberWrapper>
                          </LargeText>
                        </Column>
                      </AlignedRow>
                    </Container>
                  </Column>
                </div>
            );
          case "Article Feed":
            return (
                <Column
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                    textAlign="center"
                    flexValue={1}
                >
                  <ArticleFeed posts={posts}/>
                </Column>
            );
          case "Team Members":
            return (
                <Column
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                    flexValue={1}
                >
                  <TeamMembers teamMembers={teamMembers}></TeamMembers>
                </Column>
            );

          case "Service":
            return (
                <Column
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                    size="0"
                >
                  <div>
                    <AlignedRow>
                      {block.featureImage && (
                          <Box sx={{display: {smDown: `none`}}}>
                            <Column size="0 1rem 0 0">
                              <FeatuedImage
                                  src={block.featureImage.fields.file.url}
                                  alt={block.featureImage.fields.title}
                              />
                            </Column>
                          </Box>
                      )}
                      <Column flexValue={1} size="0 1rem 0 0">
                        {block.title ? (
                            <h3>{block.title}</h3>
                        ) : (
                            documentToReactComponents(block.content)
                        )}
                      </Column>
                    </AlignedRow>
                    {block.content && block.title
                        ? documentToReactComponents(block.content)
                        : null}
                    {block.callToAction &&
                        block.callToAction.fields.linkUrl !== "contact" && (
                            <Link href={block.callToAction.fields.linkUrl}>
                              <span>
                                <ArrowLink arrow="post">
                                  {block.callToAction.fields.linkTitle}
                                </ArrowLink>
                              </span>
                            </Link>
                        )}
                    {block.callToAction &&
                        block.callToAction.fields.linkUrl === "contact" && (
                            <PadlessButton
                                variant="text"
                                onClick={() => dispatch(appActions.toggleModal())}
                            >
                              <ArrowLink arrow="post">
                                {block.callToAction.fields.linkTitle}
                              </ArrowLink>
                            </PadlessButton>
                        )}
                  </div>
                </Column>
            );
          case "Icon Navigation":
            return (
                <CenteredGrid item
                    md={3} sm={6} xs={6}
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                >
                  {block.callToAction && (
                      <NavLink
                          onClick={() => scrollToId(block.callToAction.fields.linkUrl)}
                      >
                        {block.featureImage && (
                            <FeatuedImage
                                src={block.featureImage.fields.file.url}
                                alt={block.featureImage.fields.title}
                            />
                        )}
                        {block.title && <h3>{block.title}</h3>}
                        {block.content && documentToReactComponents(block.content)}
                        <ArrowDown/>
                      </NavLink>
                  )}
                </CenteredGrid>
            );
          case "Feature Tile":
            return (
                <CenteredGrid item
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                    md={3} sm={6} xs={6}
                >
                  <div>
                    {block.featureImage && (
                        <FeatuedImage
                            src={block.featureImage.fields.file.url}
                            alt={block.featureImage.fields.title}
                        />
                    )}
                    {block.title && <h3>{block.title}</h3>}
                    {block.content
                        ? documentToReactComponents(block.content)
                        : null}
                    {block.callToAction &&
                        block.callToAction.fields.linkUrl !== "contact" && (
                            <Link href={block.callToAction.fields.linkUrl}>
                              <span>
                                <ArrowLink arrow="post">
                                  {block.callToAction.fields.linkTitle}
                                </ArrowLink>
                              </span>
                            </Link>
                        )}
                    {block.callToAction &&
                        block.callToAction.fields.linkUrl === "contact" && (
                            <Button variant="text">
                              <ArrowLink
                                  onClick={() => dispatch(appActions.toggleModal())}
                                  arrow="post"
                              >
                                {block.callToAction.fields.linkTitle}
                              </ArrowLink>
                            </Button>
                        )}
                  </div>
                </CenteredGrid>
            );
          default:
            return (
                <Column
                    key={`section-${sectionIndex}_block-${blockIndex}`}
                    grey={block.blockType === "Grey Card"}
                >
                  <div>
                    {block.featureImage && (
                        <FeatuedImage
                            src={block.featureImage.fields.file.url}
                            alt={block.featureImage.fields.title}
                        />
                    )}
                    {block.title && <h3>{block.title}</h3>}
                    {block.content
                        ? documentToReactComponents(block.content)
                        : null}
                    {block.callToAction &&
                        block.callToAction.fields.linkUrl !== "contact" && (
                            <Link href={block.callToAction.fields.linkUrl}>
                              <span>
                                <ArrowLink arrow="post">
                                  {block.callToAction.fields.linkTitle}
                                </ArrowLink>
                              </span>
                            </Link>
                        )}
                    {block.callToAction &&
                        block.callToAction.fields.linkUrl === "contact" && (
                            <Button variant="text">
                              <ArrowLink
                                  onClick={() => dispatch(appActions.toggleModal())}
                                  arrow="post"
                              >
                                {block.callToAction.fields.linkTitle}
                              </ArrowLink>
                            </Button>
                        )}
                  </div>
                </Column>
            );
        }
      })
      .filter((b) => b !== null);
};
export default PageContainerBlocks;
